import { AuditLog, AuditLogCategory, AuditLogAction, AuditLogSeverity, AuditLogFilter } from '../types/auditLog';

const AUDIT_LOGS_KEY = 'audit_logs';
const MAX_LOGS = 10000; // Maksimum log sayısı

// Tüm logları getir
export function getAuditLogs(): AuditLog[] {
  try {
    const stored = localStorage.getItem(AUDIT_LOGS_KEY);
    if (!stored) return [];
    return JSON.parse(stored);
  } catch (error) {
    console.error('Error loading audit logs:', error);
    return [];
  }
}

// Logları kaydet
function saveAuditLogs(logs: AuditLog[]): void {
  try {
    // Maksimum log sayısını aşarsa eski logları sil
    const trimmedLogs = logs.length > MAX_LOGS 
      ? logs.slice(logs.length - MAX_LOGS) 
      : logs;
    localStorage.setItem(AUDIT_LOGS_KEY, JSON.stringify(trimmedLogs));
  } catch (error) {
    console.error('Error saving audit logs:', error);
  }
}

// Yeni log oluştur
export interface CreateAuditLogParams {
  category: AuditLogCategory;
  action: AuditLogAction;
  entity: string;
  entityId?: string;
  entityName?: string;
  description: string;
  severity?: AuditLogSeverity;
  changes?: Array<{
    field: string;
    oldValue: any;
    newValue: any;
  }>;
  metadata?: Record<string, any>;
  success?: boolean;
  errorMessage?: string;
}

export function createAuditLog(params: CreateAuditLogParams): AuditLog {
  // Mevcut kullanıcı bilgisini al
  const currentUserStr = localStorage.getItem('currentUser');
  let userId = 'system';
  let userName = 'Sistem';
  let userRole = 'system';

  if (currentUserStr) {
    try {
      const currentUser = JSON.parse(currentUserStr);
      userId = currentUser.id || 'unknown';
      userName = currentUser.name || 'Bilinmeyen Kullanıcı';
      userRole = currentUser.role || 'unknown';
    } catch (error) {
      console.error('Error parsing current user:', error);
    }
  }

  const log: AuditLog = {
    id: `log_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    timestamp: new Date().toISOString(),
    category: params.category,
    action: params.action,
    severity: params.severity || 'info',
    userId,
    userName,
    userRole,
    entity: params.entity,
    entityId: params.entityId,
    entityName: params.entityName,
    description: params.description,
    changes: params.changes,
    metadata: params.metadata,
    success: params.success !== undefined ? params.success : true,
    errorMessage: params.errorMessage,
    userAgent: navigator.userAgent,
  };

  // Log'u kaydet
  const logs = getAuditLogs();
  logs.push(log);
  saveAuditLogs(logs);

  return log;
}

// Filtrelenmiş logları getir
export function getFilteredAuditLogs(filter: AuditLogFilter): AuditLog[] {
  let logs = getAuditLogs();

  // Kategori filtresi
  if (filter.category && filter.category.length > 0) {
    logs = logs.filter(log => filter.category!.includes(log.category));
  }

  // Aksiyon filtresi
  if (filter.action && filter.action.length > 0) {
    logs = logs.filter(log => filter.action!.includes(log.action));
  }

  // Severity filtresi
  if (filter.severity && filter.severity.length > 0) {
    logs = logs.filter(log => filter.severity!.includes(log.severity));
  }

  // Kullanıcı filtresi
  if (filter.userId) {
    logs = logs.filter(log => log.userId === filter.userId);
  }

  // Entity ID filtresi
  if (filter.entityId) {
    logs = logs.filter(log => log.entityId === filter.entityId);
  }

  // Tarih filtresi
  if (filter.startDate) {
    logs = logs.filter(log => log.timestamp >= filter.startDate!);
  }

  if (filter.endDate) {
    logs = logs.filter(log => log.timestamp <= filter.endDate!);
  }

  // Arama filtresi
  if (filter.searchQuery) {
    const query = filter.searchQuery.toLowerCase();
    logs = logs.filter(log => 
      log.description.toLowerCase().includes(query) ||
      log.userName.toLowerCase().includes(query) ||
      log.entity.toLowerCase().includes(query) ||
      (log.entityName && log.entityName.toLowerCase().includes(query))
    );
  }

  // Zamana göre ters sıralama (en yeni üstte)
  return logs.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
}

// Belirli bir entity için logları getir
export function getEntityAuditLogs(entity: string, entityId: string): AuditLog[] {
  return getAuditLogs()
    .filter(log => log.entity === entity && log.entityId === entityId)
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
}

// İstatistikler
export function getAuditStats() {
  const logs = getAuditLogs();
  const now = new Date();
  const last24h = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  const last7d = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  const last30d = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

  return {
    total: logs.length,
    last24h: logs.filter(l => new Date(l.timestamp) > last24h).length,
    last7d: logs.filter(l => new Date(l.timestamp) > last7d).length,
    last30d: logs.filter(l => new Date(l.timestamp) > last30d).length,
    byCategory: logs.reduce((acc, log) => {
      acc[log.category] = (acc[log.category] || 0) + 1;
      return acc;
    }, {} as Record<string, number>),
    bySeverity: logs.reduce((acc, log) => {
      acc[log.severity] = (acc[log.severity] || 0) + 1;
      return acc;
    }, {} as Record<string, number>),
    failures: logs.filter(l => !l.success).length,
  };
}

// Logları dışa aktar
export function exportAuditLogs(filter?: AuditLogFilter): string {
  const logs = filter ? getFilteredAuditLogs(filter) : getAuditLogs();
  
  createAuditLog({
    category: 'SYSTEM',
    action: 'EXPORT',
    entity: 'AuditLogs',
    description: `${logs.length} adet log dışa aktarıldı`,
    metadata: { count: logs.length, filter },
  });

  return JSON.stringify(logs, null, 2);
}

// Tüm logları temizle
export function clearAuditLogs(): void {
  const count = getAuditLogs().length;
  
  // Son bir log kaydı oluştur
  createAuditLog({
    category: 'SYSTEM',
    action: 'DELETE',
    entity: 'AuditLogs',
    description: `Tüm sistem logları temizlendi (${count} adet)`,
    severity: 'critical',
    metadata: { clearedCount: count },
  });
  
  // localStorage'ı temizle
  localStorage.removeItem(AUDIT_LOGS_KEY);
}